import React, { useState, useEffect } from 'react';
import { 
  Mail, Eye, Edit, RotateCcw, Save, X, Code, FileText, Clock, User,
  Send, Settings, Plus, PlayCircle, PauseCircle, RefreshCw, Trash2,
  AlertCircle, CheckCircle, ChevronDown, ChevronUp, Bell, Calendar
} from 'lucide-react';
import axios from 'axios';
import { MatriculadoBuscador } from '../../components/admin/MatriculadoBuscador';
import { CampanaPortalPanel } from '../../components/admin/CampanaPortalPanel';
import { CampanaWhatsappPanel } from '../../components/admin/CampanaWhatsappPanel';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminEmailTemplatesPage() {
  // Estados para templates
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [editedHtml, setEditedHtml] = useState('');
  const [editedAsunto, setEditedAsunto] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Estados para tab activa
  const [activeTab, setActiveTab] = useState('templates');
  
  // Estados para configuración de envíos
  const [controlEnvios, setControlEnvios] = useState(null);
  const [loadingControlEnvios, setLoadingControlEnvios] = useState(false);
  const [togglingEnvio, setTogglingEnvio] = useState(null);
  
  // Estados para cuentas de email
  const [cuentaEmail, setCuentaEmail] = useState(null);
  const [loadingCuentaEmail, setLoadingCuentaEmail] = useState(false);
  
  // Estados para nuevo email personalizado
  const [showNuevoEmail, setShowNuevoEmail] = useState(false);
  const [nuevoEmailForm, setNuevoEmailForm] = useState({
    asunto: '',
    mensaje: '',
    destinatario: '' // vacío = todos los matriculados
  });
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  
  // Estados para envío individual de template
  const [showEnvioIndividual, setShowEnvioIndividual] = useState(false);
  const [envioIndividualTemplate, setEnvioIndividualTemplate] = useState(null);
  const [destinatarioIndividual, setDestinatarioIndividual] = useState('');
  const [enviandoIndividual, setEnviandoIndividual] = useState(false);
  
  // Estados adicionales para template de estado de cuenta (necesita datos ANTUAR)
  const [datosUsuarioSimular, setDatosUsuarioSimular] = useState({
    matricula: '',
    documento: '',
    fecha_nacimiento: ''
  });
  
  // Estados para reporte de errores de datos
  const [reporteErrores, setReporteErrores] = useState(null);
  const [usuariosDatosIncorrectos, setUsuariosDatosIncorrectos] = useState(null);
  const [loadingReporteErrores, setLoadingReporteErrores] = useState(false);

  useEffect(() => {
    cargarTemplates();
    cargarConfiguracion();
  }, []);
  
  const cargarConfiguracion = async () => {
    const token = localStorage.getItem('token');
    try {
      const [enviosRes, cuentaRes] = await Promise.all([
        axios.get(`${API}/api/antuar/config-envios`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/antuar/config-cuenta-email`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setControlEnvios(enviosRes.data);
      setCuentaEmail(cuentaRes.data);
      
      // Cargar reporte de errores
      cargarReporteErrores();
    } catch (err) {
      console.error('Error cargando configuración:', err);
    }
  };
  
  const cargarReporteErrores = async () => {
    const token = localStorage.getItem('token');
    setLoadingReporteErrores(true);
    try {
      const [resumenRes, usuariosRes] = await Promise.all([
        axios.get(`${API}/api/antuar/estado-cuenta-mailing/resumen-errores`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/antuar/estado-cuenta-mailing/usuarios-datos-incorrectos`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setReporteErrores(resumenRes.data);
      setUsuariosDatosIncorrectos(usuariosRes.data);
    } catch (err) {
      console.error('Error cargando reporte de errores:', err);
    } finally {
      setLoadingReporteErrores(false);
    }
  };

  const cargarTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/email-templates/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data);
    } catch (err) {
      console.error('Error cargando templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const cargarTemplate = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      // Agregar timestamp para evitar cache
      const res = await axios.get(`${API}/api/email-templates/${templateId}?_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTemplate(res.data);
      setEditedHtml(res.data.html_content);
      setEditedAsunto(res.data.asunto);
    } catch (err) {
      console.error('Error cargando template:', err);
    }
  };

  const cargarPreview = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      // Agregar timestamp para evitar cache
      const res = await axios.get(`${API}/api/email-templates/${templateId}/preview?_t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewHtml(res.data.html);
      setPreviewMode(true);
    } catch (err) {
      console.error('Error cargando preview:', err);
    }
  };

  const guardarTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/email-templates/${selectedTemplate.id}`, {
        html_content: editedHtml,
        asunto: editedAsunto
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await cargarTemplates();
      await cargarTemplate(selectedTemplate.id);
      setEditMode(false);
      alert('Template guardado correctamente');
    } catch (err) {
      console.error('Error guardando template:', err);
      alert('Error al guardar el template');
    } finally {
      setSaving(false);
    }
  };

  const resetearTemplate = async (templateId) => {
    if (!window.confirm('¿Estás seguro de restaurar este template a la versión por defecto? Se perderán todas las modificaciones.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/email-templates/${templateId}/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await cargarTemplates();
      if (selectedTemplate?.id === templateId) {
        await cargarTemplate(templateId);
      }
      alert('Template restaurado correctamente');
    } catch (err) {
      console.error('Error reseteando template:', err);
      alert('Error al restaurar el template');
    }
  };
  
  // Funciones para control de envíos
  const handleToggleEnvio = async (tipoEmail, estadoActual) => {
    const accion = estadoActual ? 'DESACTIVAR' : 'ACTIVAR';
    if (!window.confirm(`¿Estás seguro de ${accion} los envíos de este tipo de email?`)) {
      return;
    }
    
    setTogglingEnvio(tipoEmail);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/antuar/config-envios/${tipoEmail}`, 
        { activo: !estadoActual },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await cargarConfiguracion();
    } catch (err) {
      console.error('Error toggling envío:', err);
      alert('Error al cambiar estado del envío');
    } finally {
      setTogglingEnvio(null);
    }
  };
  
  const handleCambiarCuenta = async (cuentaId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/antuar/config-cuenta-email`, 
        { cuenta: cuentaId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await cargarConfiguracion();
      alert('Cuenta de email cambiada correctamente');
    } catch (err) {
      console.error('Error cambiando cuenta:', err);
      alert('Error al cambiar la cuenta de email');
    }
  };
  
  // Función para enviar email individual
  const handleEnvioIndividual = async () => {
    if (!destinatarioIndividual.trim() || !envioIndividualTemplate) {
      alert('Ingresá un email válido');
      return;
    }
    
    // Si es template de estado de cuenta, usar endpoint especial con datos ANTUAR
    if (envioIndividualTemplate.id === 'estado_cuenta_mensual') {
      if (!datosUsuarioSimular.matricula || !datosUsuarioSimular.documento || !datosUsuarioSimular.fecha_nacimiento) {
        alert('Para el template de Estado de Cuenta, debes ingresar matrícula, documento y fecha de nacimiento del usuario a simular');
        return;
      }
      
      setEnviandoIndividual(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/api/antuar/estado-cuenta-mailing/enviar-prueba`, 
          { 
            matricula: datosUsuarioSimular.matricula.trim(),
            documento: datosUsuarioSimular.documento.trim(),
            fecha_nacimiento: datosUsuarioSimular.fecha_nacimiento,
            email_destino: destinatarioIndividual.trim()
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert(`✅ Email de prueba enviado!\n\nDatos usados:\n- Nombre: ${response.data.datos_usados?.nombre}\n- Deuda total: $${response.data.datos_usados?.total_deuda?.toLocaleString()}\n- Deuda vencida: $${response.data.datos_usados?.total_deuda_vencida?.toLocaleString()}`);
        setShowEnvioIndividual(false);
        setDestinatarioIndividual('');
        setDatosUsuarioSimular({ matricula: '', documento: '', fecha_nacimiento: '' });
      } catch (err) {
        console.error('Error enviando email:', err);
        alert(err.response?.data?.detail || 'Error al enviar el email');
      } finally {
        setEnviandoIndividual(false);
      }
      return;
    }
    
    // Para otros templates, usar endpoint genérico
    setEnviandoIndividual(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/email-templates/${envioIndividualTemplate.id}/enviar`, 
        { destinatario: destinatarioIndividual.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Email enviado correctamente');
      setShowEnvioIndividual(false);
      setDestinatarioIndividual('');
    } catch (err) {
      console.error('Error enviando email:', err);
      alert(err.response?.data?.detail || 'Error al enviar el email');
    } finally {
      setEnviandoIndividual(false);
    }
  };
  
  // Función para enviar email nuevo personalizado
  const handleEnviarNuevoEmail = async () => {
    if (!nuevoEmailForm.asunto.trim() || !nuevoEmailForm.mensaje.trim()) {
      alert('El asunto y mensaje son obligatorios');
      return;
    }
    
    setEnviandoEmail(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/email-templates/enviar-personalizado`, nuevoEmailForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Email enviado correctamente');
      setShowNuevoEmail(false);
      setNuevoEmailForm({ asunto: '', mensaje: '', destinatario: '' });
    } catch (err) {
      console.error('Error enviando email:', err);
      alert(err.response?.data?.detail || 'Error al enviar el email');
    } finally {
      setEnviandoEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Mail className="h-6 w-6 text-violet-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestión de Emails</h1>
                <p className="text-gray-500">Templates, configuración y envíos del sistema</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowNuevoEmail(true)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nuevo Email
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'templates' 
                ? 'border-violet-600 text-violet-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Templates de Email
          </button>
          <button
            onClick={() => setActiveTab('configuracion')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'configuracion' 
                ? 'border-violet-600 text-violet-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Control de Envíos
          </button>
          <button
            onClick={() => setActiveTab('envioRapido')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'envioRapido' 
                ? 'border-violet-600 text-violet-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Envío Rápido
          </button>
          <button
            onClick={() => setActiveTab('campana')}
            data-testid="tab-campana-portal"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'campana'
                ? 'border-violet-600 text-violet-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Campaña Sumate al Portal
          </button>
        </div>
      </div>

      {/* Contenido según tab */}
      {activeTab === 'envioRapido' ? (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Enviar email personalizado */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-violet-500" />
                  Email Personalizado
                </h2>
                <p className="text-sm text-gray-500 mt-1">Enviá un email con asunto y mensaje personalizado</p>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Destinatario
                  </label>
                  <input
                    type="email"
                    value={nuevoEmailForm.destinatario}
                    onChange={(e) => setNuevoEmailForm({...nuevoEmailForm, destinatario: e.target.value})}
                    placeholder="email@ejemplo.com (vacío = todos)"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Dejalo vacío para enviar a todos los matriculados
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asunto <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={nuevoEmailForm.asunto}
                    onChange={(e) => setNuevoEmailForm({...nuevoEmailForm, asunto: e.target.value})}
                    placeholder="Asunto del email"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mensaje <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={nuevoEmailForm.mensaje}
                    onChange={(e) => setNuevoEmailForm({...nuevoEmailForm, mensaje: e.target.value})}
                    rows={6}
                    placeholder="Escribí el contenido del email..."
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                
                {!nuevoEmailForm.destinatario && nuevoEmailForm.asunto && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>Se enviará a TODOS los matriculados con email</span>
                    </p>
                  </div>
                )}
                
                <button
                  onClick={handleEnviarNuevoEmail}
                  disabled={enviandoEmail || !nuevoEmailForm.asunto.trim() || !nuevoEmailForm.mensaje.trim()}
                  className="w-full px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {enviandoEmail ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar Email
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Enviar template a un email */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-violet-500" />
                  Enviar Template Individual
                </h2>
                <p className="text-sm text-gray-500 mt-1">Seleccioná un template y envialo a un email específico</p>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Seleccionar Template <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={envioIndividualTemplate?.id || ''}
                    onChange={(e) => {
                      const template = templates.find(t => t.id === e.target.value);
                      setEnvioIndividualTemplate(template || null);
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="">Seleccionar...</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email del destinatario <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={destinatarioIndividual}
                    onChange={(e) => setDestinatarioIndividual(e.target.value)}
                    placeholder="email@ejemplo.com"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                
                {envioIndividualTemplate && (
                  <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                    <p className="text-sm text-violet-800">
                      <strong>Template:</strong> {envioIndividualTemplate.nombre}
                    </p>
                    <p className="text-xs text-violet-600 mt-1">
                      {envioIndividualTemplate.descripcion}
                    </p>
                  </div>
                )}
                
                {/* Campos adicionales para Estado de Cuenta (requiere datos ANTUAR) */}
                {envioIndividualTemplate?.id === 'estado_cuenta_mensual' && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertCircle className="w-4 h-4" />
                      <p className="text-sm font-medium">Datos del usuario a simular</p>
                    </div>
                    <p className="text-xs text-amber-700">
                      Buscá al matriculado o ingresá sus datos a mano. El email se enviará al destinatario de arriba.
                    </p>
                    <MatriculadoBuscador onSelect={(u) => setDatosUsuarioSimular({
                      matricula: u?.matricula || '', documento: u?.documento || '', fecha_nacimiento: u?.fecha_nacimiento || '',
                    })} />
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Matrícula <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={datosUsuarioSimular.matricula}
                          onChange={(e) => setDatosUsuarioSimular(prev => ({...prev, matricula: e.target.value}))}
                          placeholder="Ej: 13509"
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Documento (DNI) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={datosUsuarioSimular.documento}
                          onChange={(e) => setDatosUsuarioSimular(prev => ({...prev, documento: e.target.value}))}
                          placeholder="Ej: 29909564"
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Fecha de Nacimiento <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={datosUsuarioSimular.fecha_nacimiento}
                          onChange={(e) => setDatosUsuarioSimular(prev => ({...prev, fecha_nacimiento: e.target.value}))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                <button
                  onClick={handleEnvioIndividual}
                  disabled={enviandoIndividual || !envioIndividualTemplate || !destinatarioIndividual.trim()}
                  className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {enviandoIndividual ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar Template
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'campana' ? (
        <>
        <CampanaPortalPanel onPreview={async () => {
          setActiveTab('templates');
          setEditMode(false);
          await cargarTemplate('campana_sumate_portal');
          await cargarPreview('campana_sumate_portal');
        }} />
        <CampanaWhatsappPanel />
        </>
      ) : activeTab === 'configuracion' ? (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Control de envíos automáticos */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-violet-500" />
                  Control de Envíos Automáticos
                </h2>
                <p className="text-sm text-gray-500 mt-1">Activá o desactivá los tipos de email automático</p>
              </div>
              <div className="divide-y">
                {controlEnvios && Array.isArray(controlEnvios) && controlEnvios.map((config) => (
                  <div key={config.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{config.nombre || config.id}</p>
                      <p className="text-sm text-gray-500">{config.descripcion || ''}</p>
                    </div>
                    <button
                      onClick={() => handleToggleEnvio(config.id, config.activo)}
                      disabled={togglingEnvio === config.id}
                      className={`p-2 rounded-lg transition-colors ${
                        config.activo 
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                    >
                      {config.activo ? (
                        <PlayCircle className="w-5 h-5" />
                      ) : (
                        <PauseCircle className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                ))}
                {!controlEnvios && (
                  <div className="p-4 text-center text-gray-500">
                    Cargando configuración...
                  </div>
                )}
              </div>
            </div>

            {/* Cuenta de email activa */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-violet-500" />
                  Cuenta de Email Activa
                </h2>
                <p className="text-sm text-gray-500 mt-1">Seleccioná qué cuenta usa el sistema</p>
              </div>
              <div className="p-4">
                {cuentaEmail ? (
                  <div className="space-y-3">
                    {cuentaEmail.cuentas_disponibles?.map((cuenta) => (
                      <div 
                        key={cuenta.id}
                        onClick={() => handleCambiarCuenta(cuenta.id)}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          cuentaEmail.cuenta_activa === cuenta.id
                            ? 'border-violet-500 bg-violet-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{cuenta.nombre}</p>
                            <p className="text-sm text-gray-500">{cuenta.email}</p>
                          </div>
                          {cuentaEmail.cuenta_activa === cuenta.id && (
                            <CheckCircle className="w-5 h-5 text-violet-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500">Cargando...</div>
                )}
              </div>
            </div>
          </div>
          
          {/* Reporte de errores de datos - Estado de Cuenta */}
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    Usuarios con Datos Incorrectos (Estado de Cuenta)
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Usuarios que no pudieron recibir su estado de cuenta porque ANTUAR no reconoce sus datos
                  </p>
                </div>
                <button
                  onClick={cargarReporteErrores}
                  disabled={loadingReporteErrores}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingReporteErrores ? 'animate-spin' : ''}`} />
                  Actualizar
                </button>
              </div>
              
              {/* Resumen */}
              {reporteErrores && (
                <div className="p-4 border-b bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-600">{reporteErrores.resumen?.completados || 0}</p>
                      <p className="text-xs text-gray-500">Enviados OK</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{reporteErrores.resumen?.datos_incorrectos || 0}</p>
                      <p className="text-xs text-gray-500">Datos Incorrectos</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{reporteErrores.resumen?.errores_conexion || 0}</p>
                      <p className="text-xs text-gray-500">Errores Conexión</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{reporteErrores.resumen?.pendientes || 0}</p>
                      <p className="text-xs text-gray-500">Pendientes</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Lista de usuarios con datos incorrectos */}
              <div className="divide-y max-h-[400px] overflow-y-auto">
                {loadingReporteErrores ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
                    <p className="text-sm text-gray-500 mt-2">Cargando...</p>
                  </div>
                ) : usuariosDatosIncorrectos?.usuarios?.length > 0 ? (
                  usuariosDatosIncorrectos.usuarios.map((usuario, idx) => (
                    <div key={idx} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{usuario.nombre || 'Sin nombre'}</p>
                          <div className="mt-1 text-sm text-gray-500 space-y-0.5">
                            <p><strong>Matrícula:</strong> {usuario.matricula}</p>
                            <p><strong>DNI:</strong> {usuario.documento}</p>
                            <p><strong>Fecha Nac:</strong> {usuario.fecha_nacimiento}</p>
                            <p><strong>Email:</strong> {usuario.email}</p>
                          </div>
                          <p className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded inline-block">
                            {usuario.error}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto" />
                    <p className="text-sm text-gray-600 mt-2">No hay usuarios con datos incorrectos</p>
                    <p className="text-xs text-gray-400 mt-1">Todos los datos fueron validados correctamente por ANTUAR</p>
                  </div>
                )}
              </div>
              
              {usuariosDatosIncorrectos?.usuarios?.length > 0 && (
                <div className="p-4 bg-amber-50 border-t border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Acción sugerida:</strong> {usuariosDatosIncorrectos.accion_sugerida}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Tab Templates - contenido original */
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lista de templates */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="p-4 border-b">
                  <h2 className="font-semibold text-gray-900">Emails del Sistema</h2>
                </div>
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedTemplate?.id === template.id ? 'bg-violet-50 border-l-4 border-violet-600' : ''
                      }`}
                      onClick={() => {
                        cargarTemplate(template.id);
                        setEditMode(false);
                        setPreviewMode(false);
                      }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 text-sm">{template.nombre}</h3>
                        <p className="text-xs text-gray-500 mt-1">{template.descripcion}</p>
                      </div>
                      {template.personalizado && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                          Modificado
                        </span>
                      )}
                    </div>
                    {template.ultima_modificacion && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                        <Clock className="h-3 w-3" />
                        <span>{template.ultima_modificacion}</span>
                        <User className="h-3 w-3 ml-2" />
                        <span>{template.modificado_por}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Panel de detalle/edición */}
          <div className="lg:col-span-2">
            {selectedTemplate ? (
              <div className="bg-white rounded-xl shadow-sm border">
                {/* Header del template */}
                <div className="p-4 border-b flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-gray-900">{selectedTemplate.nombre}</h2>
                    <p className="text-sm text-gray-500">{selectedTemplate.descripcion}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!editMode && !previewMode && (
                      <>
                        <button
                          onClick={() => cargarPreview(selectedTemplate.id)}
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                          <Eye className="h-4 w-4" />
                          Preview
                        </button>
                        <button
                          onClick={() => setEditMode(true)}
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                        >
                          <Edit className="h-4 w-4" />
                          Editar
                        </button>
                        {selectedTemplate.personalizado && (
                          <button
                            onClick={() => resetearTemplate(selectedTemplate.id)}
                            className="flex items-center gap-2 px-3 py-2 text-sm bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200"
                          >
                            <RotateCcw className="h-4 w-4" />
                            Restaurar
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEnvioIndividualTemplate(selectedTemplate);
                            setShowEnvioIndividual(true);
                          }}
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <Send className="h-4 w-4" />
                          Enviar
                        </button>
                      </>
                    )}
                    {(editMode || previewMode) && (
                      <button
                        onClick={() => {
                          setEditMode(false);
                          setPreviewMode(false);
                          setEditedHtml(selectedTemplate.html_content);
                          setEditedAsunto(selectedTemplate.asunto);
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        <X className="h-4 w-4" />
                        Cancelar
                      </button>
                    )}
                    {editMode && (
                      <button
                        onClick={guardarTemplate}
                        disabled={saving}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        <Save className="h-4 w-4" />
                        {saving ? 'Guardando...' : 'Guardar'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Contenido */}
                <div className="p-4">
                  {/* Modo Preview */}
                  {previewMode && (
                    <div>
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Vista previa</strong> - Así se verá el email con datos de ejemplo
                        </p>
                      </div>
                      <div 
                        className="border rounded-lg overflow-hidden"
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                      />
                    </div>
                  )}

                  {/* Modo Edición */}
                  {editMode && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Asunto del Email
                        </label>
                        <input
                          type="text"
                          value={editedAsunto}
                          onChange={(e) => setEditedAsunto(e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                      </div>
                      
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700">
                            HTML del Email
                          </label>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Code className="h-3 w-3" />
                            Variables disponibles: {selectedTemplate.variables.map(v => `{{${v}}}`).join(', ')}
                          </div>
                        </div>
                        <textarea
                          value={editedHtml}
                          onChange={(e) => setEditedHtml(e.target.value)}
                          rows={20}
                          className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Modo Vista Normal */}
                  {!editMode && !previewMode && (
                    <div className="space-y-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">
                          <strong>Asunto:</strong> {selectedTemplate.asunto}
                        </p>
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-700">Variables disponibles</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedTemplate.variables.map((v) => (
                            <span key={v} className="px-2 py-1 bg-violet-100 text-violet-700 text-xs rounded-full font-mono">
                              {`{{${v}}}`}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <p className="text-sm text-gray-500 mb-3">
                          Hacé clic en "Preview" para ver cómo se ve el email, o en "Editar" para modificar el HTML.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Seleccioná un template</h3>
                <p className="text-gray-500">
                  Elegí un template de la lista para ver su preview o editarlo
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
      {/* Modal: Nuevo Email Personalizado */}
      {showNuevoEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-violet-500" />
                Nuevo Email Personalizado
              </h2>
              <button
                onClick={() => setShowNuevoEmail(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destinatario
                </label>
                <input
                  type="email"
                  value={nuevoEmailForm.destinatario}
                  onChange={(e) => setNuevoEmailForm({...nuevoEmailForm, destinatario: e.target.value})}
                  placeholder="email@ejemplo.com (dejar vacío para enviar a todos los matriculados)"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Si dejás vacío, se enviará a todos los matriculados con email registrado
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asunto <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nuevoEmailForm.asunto}
                  onChange={(e) => setNuevoEmailForm({...nuevoEmailForm, asunto: e.target.value})}
                  placeholder="Asunto del email"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensaje <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={nuevoEmailForm.mensaje}
                  onChange={(e) => setNuevoEmailForm({...nuevoEmailForm, mensaje: e.target.value})}
                  rows={8}
                  placeholder="Escribí el contenido del email..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  El texto se enviará con formato básico (sin HTML). Los saltos de línea se respetan.
                </p>
              </div>
              
              {!nuevoEmailForm.destinatario && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <strong>Atención:</strong> Este email se enviará a TODOS los matriculados con email registrado.
                  </p>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowNuevoEmail(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleEnviarNuevoEmail}
                disabled={enviandoEmail || !nuevoEmailForm.asunto.trim() || !nuevoEmailForm.mensaje.trim()}
                className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {enviandoEmail ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Enviar Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Envío Individual de Template */}
      {showEnvioIndividual && envioIndividualTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-violet-500" />
                Enviar Template
              </h2>
              <button
                onClick={() => {
                  setShowEnvioIndividual(false);
                  setDestinatarioIndividual('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                <p className="text-sm text-violet-800">
                  <strong>Template:</strong> {envioIndividualTemplate.nombre}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email del destinatario <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={destinatarioIndividual}
                  onChange={(e) => setDestinatarioIndividual(e.target.value)}
                  placeholder="email@ejemplo.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="text-xs text-gray-600">
                  El template se enviará con datos de ejemplo. Si el destinatario es un matriculado, sus datos se precargarán automáticamente.
                </p>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowEnvioIndividual(false);
                  setDestinatarioIndividual('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleEnvioIndividual}
                disabled={enviandoIndividual || !destinatarioIndividual.trim()}
                className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {enviandoIndividual ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Enviar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
